document.addEventListener('DOMContentLoaded', function () {
    const smoothScrollStorageKey = 'smooth-scroll-target';
    const scrollKeys = new Set([
        'ArrowDown',
        'ArrowUp',
        'End',
        'Enter',
        'Home',
        'PageDown',
        'PageUp',
        ' '
    ]);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let scheduledScrollCleanup = null;
    let activeScrollTimer = null;
    let isProgrammaticScrollActive = false;

    const cancelScheduledScroll = function () {
        if (scheduledScrollCleanup) {
            scheduledScrollCleanup();
            scheduledScrollCleanup = null;
        }
    };

    const stopProgrammaticScroll = function (freezePosition = false) {
        if (activeScrollTimer !== null) {
            window.clearTimeout(activeScrollTimer);
            activeScrollTimer = null;
        }

        if (freezePosition && isProgrammaticScrollActive) {
            window.scrollTo({
                top: window.scrollY,
                behavior: 'auto'
            });
        }

        isProgrammaticScrollActive = false;
    };

    const interruptActiveScroll = function () {
        cancelScheduledScroll();
        stopProgrammaticScroll(true);
    };

    window.addEventListener('pointerdown', interruptActiveScroll, {
        capture: true,
        passive: true
    });
    window.addEventListener('touchstart', interruptActiveScroll, {
        capture: true,
        passive: true
    });
    window.addEventListener('wheel', interruptActiveScroll, {
        capture: true,
        passive: true
    });
    window.addEventListener('keydown', function (event) {
        if (scrollKeys.has(event.key)) {
            interruptActiveScroll();
        }
    }, true);

    const normalizePathname = function (pathname) {
        if (pathname === '/index.html') {
            return '/';
        }

        return pathname.replace(/\/+$/, '') || '/';
    };

    const getTargetByHash = function (hash) {
        const targetID = decodeURIComponent(hash.substring(1));
        return document.getElementById(targetID);
    };

    const shouldReduceMotion = function () {
        return reducedMotion.matches
            || document.body.dataset.accessibility === 'true'
            || document.body.dataset.noImages === 'true';
    };

    const getHeaderOffset = function () {
        const header = document.querySelector('.header');
        const headerHeight = header ? header.getBoundingClientRect().height : 0;

        return headerHeight + 40;
    };

    const scrollToTarget = function (targetElement) {
        const headerOffset = getHeaderOffset();
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        const behavior = shouldReduceMotion() ? 'auto' : 'smooth';

        stopProgrammaticScroll();
        isProgrammaticScrollActive = true;
        window.scrollTo({
            top: offsetPosition,
            behavior
        });

        let lastTargetDocumentTop = null;
        let stablePositionChecks = 0;

        const monitorTargetPosition = function (checksLeft, delay) {
            activeScrollTimer = window.setTimeout(() => {
                activeScrollTimer = null;

                if (!isProgrammaticScrollActive) {
                    return;
                }

                const targetRect = targetElement.getBoundingClientRect();
                const targetDocumentTop = targetRect.top + window.scrollY;
                const positionDifference = targetRect.top - getHeaderOffset();

                if (
                    lastTargetDocumentTop !== null
                    && Math.abs(targetDocumentTop - lastTargetDocumentTop) <= 1
                ) {
                    stablePositionChecks += 1;
                } else {
                    stablePositionChecks = 0;
                }

                lastTargetDocumentTop = targetDocumentTop;

                if (
                    stablePositionChecks >= 4
                    && Math.abs(positionDifference) <= 1
                ) {
                    isProgrammaticScrollActive = false;
                    return;
                }

                if (
                    stablePositionChecks >= 2
                    && Math.abs(positionDifference) > 1
                ) {
                    window.scrollTo({
                        top: window.scrollY + positionDifference,
                        behavior
                    });
                    stablePositionChecks = 0;
                }

                if (checksLeft === 0) {
                    activeScrollTimer = window.setTimeout(() => {
                        activeScrollTimer = null;
                        isProgrammaticScrollActive = false;
                    }, 900);
                    return;
                }

                monitorTargetPosition(checksLeft - 1, 250);
            }, delay);
        };

        monitorTargetPosition(16, 1300);
    };

    const scrollToTargetAfterLayout = function (targetElement, options = {}) {
        const waitForPageLoad = Boolean(options.waitForPageLoad);
        const hashToRestore = options.hashToRestore || '';
        let firstFrame = null;
        let secondFrame = null;
        let loadHandler = null;
        let isCancelled = false;

        const cleanup = function () {
            if (isCancelled) {
                return;
            }

            isCancelled = true;

            if (loadHandler) {
                window.removeEventListener('load', loadHandler);
                loadHandler = null;
            }

            if (firstFrame !== null) {
                window.cancelAnimationFrame(firstFrame);
                firstFrame = null;
            }

            if (secondFrame !== null) {
                window.cancelAnimationFrame(secondFrame);
                secondFrame = null;
            }

            if (scheduledScrollCleanup === cleanup) {
                scheduledScrollCleanup = null;
            }
        };

        const startScroll = function () {
            const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();

            fontsReady.catch(() => undefined).then(() => {
                if (isCancelled) {
                    return;
                }

                firstFrame = window.requestAnimationFrame(() => {
                    firstFrame = null;
                    secondFrame = window.requestAnimationFrame(() => {
                        secondFrame = null;

                        if (isCancelled) {
                            return;
                        }

                        if (hashToRestore) {
                            window.history.replaceState(
                                window.history.state,
                                '',
                                hashToRestore
                            );
                        }

                        cleanup();
                        scrollToTarget(targetElement);
                    });
                });
            });
        };

        cancelScheduledScroll();
        scheduledScrollCleanup = cleanup;

        if (waitForPageLoad && document.readyState !== 'complete') {
            loadHandler = startScroll;
            window.addEventListener('load', loadHandler, { once: true });
            return;
        }

        startScroll();
    };

    const restoreInitialHashScroll = function () {
        const savedTarget = sessionStorage.getItem(smoothScrollStorageKey);
        let targetHash = window.location.hash;

        if (savedTarget) {
            let targetUrl;

            try {
                targetUrl = JSON.parse(savedTarget);
            } catch (error) {
                sessionStorage.removeItem(smoothScrollStorageKey);
                return;
            }

            if (
                normalizePathname(targetUrl.pathname)
                !== normalizePathname(window.location.pathname)
            ) {
                sessionStorage.removeItem(smoothScrollStorageKey);
            } else {
                sessionStorage.removeItem(smoothScrollStorageKey);
                targetHash = targetUrl.hash;
            }
        }

        if (!targetHash) {
            return;
        }

        const targetElement = getTargetByHash(targetHash);

        if (!targetElement) {
            return;
        }

        window.history.replaceState(
            window.history.state,
            '',
            window.location.pathname + window.location.search
        );
        window.scrollTo({
            top: 0,
            behavior: 'auto'
        });
        scrollToTargetAfterLayout(targetElement, {
            waitForPageLoad: true,
            hashToRestore: targetHash
        });
    };

    restoreInitialHashScroll();

    document.addEventListener('click', function (event) {
        if (
            event.defaultPrevented
            || event.button !== 0
            || event.metaKey
            || event.ctrlKey
            || event.shiftKey
            || event.altKey
        ) {
            return;
        }

        const anchor = event.target.closest('a[href]');

        if (
            !anchor
            || anchor.hasAttribute('download')
            || (anchor.target && anchor.target !== '_self')
        ) {
            return;
        }

        const targetUrl = new URL(anchor.href, window.location.href);
        const isSameOrigin = targetUrl.origin === window.location.origin;
        const isSamePage = isSameOrigin
            && normalizePathname(targetUrl.pathname)
                === normalizePathname(window.location.pathname);

        if (!isSameOrigin || !targetUrl.hash) {
            return;
        }

        if (!isSamePage) {
            event.preventDefault();
            sessionStorage.setItem(smoothScrollStorageKey, JSON.stringify({
                pathname: targetUrl.pathname,
                hash: targetUrl.hash
            }));
            window.location.assign(targetUrl.pathname + targetUrl.search);
            return;
        }

        const targetElement = getTargetByHash(targetUrl.hash);

        if (!targetElement) {
            return;
        }

        event.preventDefault();

        if (window.location.hash === targetUrl.hash) {
            window.history.replaceState(window.history.state, '', targetUrl.hash);
        } else {
            window.history.pushState(window.history.state, '', targetUrl.hash);
        }

        scrollToTargetAfterLayout(targetElement);
    });
});
